import React, { useContext, useEffect, useRef, useState } from "react";
import Chip from "@material-ui/core/Chip";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import useSocketListener from "../../hooks/useSocketListener";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import { canAccessTaskControl, canViewTasksList, normalizeId } from "../../pages/Tasks/taskUtils";

const useStyles = makeStyles(theme => ({
  chip: {
    marginLeft: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      marginTop: theme.spacing(0.5)
    }
  }
}));

// Indicador discreto "N tarefas abertas" no cabeçalho do atendimento.
// Só aparece quando plano+permissão permitem E existe pelo menos uma
// tarefa aberta (pending/in_progress) do contato — nunca antes disso, e
// nunca reaproveitando o plano/contador de um contato ou empresa anterior.
// O clique é responsabilidade do componente pai (onClick) — este
// componente não conhece o ContactDrawer nem duplica nenhuma listagem.
const TicketOpenTasksIndicator = ({ contactId, onClick }) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  const normalizedContactId = normalizeId(contactId);

  // plan === null cobre tanto "ainda não carregou" quanto "a consulta
  // falhou" — nos dois casos o indicador fica oculto (mesmo padrão
  // null-until-loaded já usado em ContactDrawer/TicketActionButtonsCustom).
  const [plan, setPlan] = useState(null);
  const [openTotal, setOpenTotal] = useState(0);

  const isMountedRef = useRef(true);
  const planRequestIdRef = useRef(0);
  const summaryRequestIdRef = useRef(0);
  const debounceRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const allowed = canAccessTaskControl({ plan, user }) && canViewTasksList({ user });

  // Plano: reiniciado sempre que o contato OU a empresa mudar — nunca
  // mantém o plano do contato/empresa anterior visível enquanto a nova
  // consulta está em andamento (por isso setPlan(null) roda antes de
  // qualquer chamada, de forma síncrona, e não só no catch).
  useEffect(() => {
    const currentRequestId = ++planRequestIdRef.current;
    setPlan(null);
    setOpenTotal(0);

    if (normalizedContactId === null || !user?.companyId) return undefined;

    (async () => {
      try {
        const planConfigs = await getPlanCompany(undefined, user.companyId);
        if (!isMountedRef.current || currentRequestId !== planRequestIdRef.current) return;
        setPlan(planConfigs.plan);
      } catch (err) {
        // Falha na consulta do plano: sem rejeição não tratada e sem
        // toast — o indicador é discreto por natureza, então uma falha
        // aqui apenas o mantém oculto (plan permanece null).
      }
    })();

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedContactId, user?.companyId]);

  const fetchSummary = () => {
    if (normalizedContactId === null || !allowed) return;

    const currentRequestId = ++summaryRequestIdRef.current;

    (async () => {
      try {
        const { data } = await api.get("/tasks/summary", {
          params: { contactId: normalizedContactId }
        });
        if (!isMountedRef.current || currentRequestId !== summaryRequestIdRef.current) return;
        setOpenTotal(Number(data.openTotal) || 0);
      } catch (err) {
        if (!isMountedRef.current || currentRequestId !== summaryRequestIdRef.current) return;
        setOpenTotal(0);
      }
    })();
  };

  useEffect(() => {
    summaryRequestIdRef.current += 1;
    setOpenTotal(0);
    if (allowed) fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedContactId, allowed]);

  // Limpa o timer de debounce ao desmontar E sempre que o contato mudar —
  // nunca deixa um timer antigo, agendado para o contato anterior, disparar
  // depois da troca.
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [normalizedContactId]);

  // Sinal opaco ({action:"refresh"}) — nunca lê dados do evento, só
  // dispara uma nova consulta pelo endpoint autenticado. Vários eventos em
  // sequência geram no máximo uma atualização.
  const handleRealtimeTaskEvent = () => {
    if (normalizedContactId === null || !allowed) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSummary();
    }, 500);
  };

  useSocketListener(socket, user, "task", handleRealtimeTaskEvent, [
    normalizedContactId,
    allowed
  ]);

  if (!allowed || normalizedContactId === null || openTotal <= 0) return null;

  const label =
    openTotal === 1
      ? i18n.t("tasks.ticketIndicator.singular")
      : i18n.t("tasks.ticketIndicator.plural", { count: openTotal });

  // stopPropagation defensivo: o indicador é renderizado como irmão do
  // TicketInfo (não como filho), então não há bubbling real para o
  // onClick do cabeçalho hoje — mas isso evita qualquer regressão futura
  // se a estrutura mudar. Não é preciso tratar teclado manualmente: com
  // onClick definido, o Chip renderiza como ButtonBase, que já ativa
  // onClick via Enter/Espaço nativamente — um onKeyDown próprio chamando
  // onClick() de novo disparava o clique em dobro.
  const handleClick = e => {
    e.stopPropagation();
    onClick();
  };

  return (
    <Chip
      className={classes.chip}
      size="small"
      color="primary"
      variant="outlined"
      icon={<AssignmentTurnedInIcon fontSize="small" />}
      label={label}
      clickable
      onClick={handleClick}
      aria-label={label}
    />
  );
};

export default TicketOpenTasksIndicator;
